import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './Modal.css';

// Simple modal to create a custom variable (derived expression)
// Props:
//  open: bool
//  onClose(): void
//  onCreate({ name, expression, dependencies }): void
//  availableColumns: array of column names (strings)
export default function CustomVariableModal({ open, onClose, onCreate, availableColumns }) {
  const [name, setName] = useState('');
  const [expression, setExpression] = useState('');
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (open) { setName(''); setExpression(''); setError(''); setFilter(''); }
  }, [open]);

  if (!open) return null;

  const reservedNames = new Set(['dt','sqrt']);

  // Extract variable tokens to infer dependencies
  const parseDependencies = (expr) => {
    if (!expr) return [];
    const tokens = expr.match(/[A-Za-z_][A-Za-z0-9_.]*/g) || [];
    const deps = new Set();
    tokens.forEach(t => {
      if (reservedNames.has(t)) return;
      // exclude numeric-like tokens (already filtered by regex) and function names
      if (availableColumns.includes(t)) deps.add(t);
    });
    return Array.from(deps);
  };

  const handleCreate = () => {
    setError('');
    const trimmedName = name.trim();
    if (!trimmedName) { setError('Name required'); return; }
    if (/\s/.test(trimmedName)) { setError('No spaces in name'); return; }
    if (reservedNames.has(trimmedName)) { setError('Name is reserved'); return; }
    if (!expression.trim()) { setError('Expression required'); return; }
    const deps = parseDependencies(expression);
    if (deps.length === 0) { setError('Expression must reference at least one base column'); return; }
    try {
      onCreate({ name: trimmedName, expression: expression.trim(), dependencies: deps });
    } catch (e) {
      setError('Failed to create: ' + e.message);
      return;
    }
    onClose();
  };

  const filteredCols = availableColumns.filter(c => !filter || c.toLowerCase().includes(filter.toLowerCase()));

  return ReactDOM.createPortal(
    <div className="modal-overlay" role="presentation">
      <div className="modal-content" role="dialog" aria-modal="true" aria-label="Create custom variable">
        <h2 style={{marginTop:0,fontSize:'1rem'}}>Create Custom Variable</h2>
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          <label className="modal-field" style={{fontSize:'0.7rem'}}>Name
            <input type="text" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. magnitude_accel" />
          </label>
          <label className="modal-field" style={{fontSize:'0.7rem'}}>Expression
            <textarea rows={4} value={expression} onChange={e=>setExpression(e.target.value)} placeholder="(vehicle_local_position.ax^2 + vehicle_local_position.ay^2 + vehicle_local_position.az^2)^0.5" />
          </label>
          <div className="modal-hint" style={{fontSize:'0.6rem', lineHeight:1.2, background:'#f5f5f5', padding:'4px 6px'}}>
            Operators: + - * / ^  | Functions: sqrt(x), dt(x).<br/>
            Use parentheses. Variables must match telemetry column names exactly.<br/>
            Derivative dt(var) uses delta between successive samples / delta time.
          </div>
          <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
            <input type="text" className="filter-input compact" style={{flex:1}} placeholder="Filter columns" value={filter} onChange={e=>setFilter(e.target.value)} />
          </div>
          <div className="custom-columns-list" style={{maxHeight:'140px', overflow:'auto', border:'1px solid #ccc', padding:'4px'}}>
            {filteredCols.map(c => (
              <div key={c} className="custom-col-item" style={{fontSize:'0.6rem', padding:'2px 0'}}>{c}</div>
            ))}
            {filteredCols.length === 0 && <div style={{fontSize:'0.6rem', opacity:0.7}}>No matches</div>}
          </div>
          {error && <div className="graph-error" style={{marginTop:'4px'}}>{error}</div>}
        </div>
        <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'12px' }}>
          <button type="button" className="toggle-video-button" onClick={onClose}>Cancel</button>
          <button type="button" className="toggle-video-button" onClick={handleCreate}>Create</button>
        </div>
        <button aria-label="Close" onClick={onClose} style={{position:'absolute', top:4, right:8, background:'transparent', border:'none', fontSize:'1rem', cursor:'pointer'}}>×</button>
      </div>
    </div>,
    document.body
  );
}
